/**
 * lib/prontoContacts.js
 * ------------------------------------------------------------------
 * Auto-sync della Rubrica CRM Pronto → Contact Provider iOS 18+.
 *
 * FLOW UTENTE (invisibile):
 *   1. Utente apre l'app Pronto la prima volta su iOS 18+
 *   2. Al mount della pagina Rubrica, il codice chiama `ensureEnabled()`
 *      → iOS mostra il prompt di sistema "Pronto vuole fornire contatti
 *        a questo iPhone" — tap Consenti
 *   3. `syncFromRubrica(contatti)` scrive nell'App Group e chiama
 *      `signalEnumerator()`
 *   4. Da qui in poi, ogni volta che l'utente crea/modifica/elimina un
 *      cliente in Pronto, chiamiamo `syncFromRubrica(contatti)` di nuovo
 *   5. WhatsApp/Chiamate mostrano il nome del cliente Pronto quando quel
 *      numero riceve un messaggio o parte una chiamata
 *
 * Su piattaforme diverse da iOS 18+ il modulo degrada silenziosamente:
 * `ensureEnabled`/`syncFromRubrica` ritornano `{ supported: false }` senza
 * far niente, senza errori né toast.
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

const isNative =
  typeof Capacitor !== 'undefined' &&
  Capacitor.isNativePlatform &&
  Capacitor.isNativePlatform() &&
  Capacitor.getPlatform() === 'ios';

const Native = isNative ? registerPlugin('ProntoContacts') : null;

// Cache in-memory dello status per non chiamare getStatus() a ogni tap
let cachedStatus = null;
// Flag per evitare di ri-chiedere enable() più di una volta per sessione
let enableAttempted = false;
// Ultimo hash del payload sincronizzato — evita call ridondanti
let lastSyncHash = null;

export function isProntoContactsAvailable() {
  return !!Native;
}

// iter v33 r08 — DEBUG: last error observed during ensureEnabled/enable/sync.
// Consumato dal pannello debug in Contatti.jsx per far vedere all'utente
// (o allo sviluppatore da remoto) cosa sta bloccando l'attivazione.
let lastError = null;
export function getLastError() {
  return lastError;
}
export function getDebugSnapshot() {
  return {
    isNative,
    platform: typeof Capacitor !== 'undefined' ? Capacitor.getPlatform?.() : 'unknown',
    pluginRegistered: !!Native,
    pluginAvailable: (typeof Capacitor !== 'undefined' && Capacitor.isPluginAvailable)
      ? Capacitor.isPluginAvailable('ProntoContacts') : null,
    enableAttempted,
    cachedStatus,
    lastError,
    lastSyncHash: lastSyncHash ? `${lastSyncHash.slice(0, 40)}${lastSyncHash.length > 40 ? '…' : ''}` : null,
  };
}

export async function getStatus() {
  if (!Native) {
    return { supported: false, enabled: false, count: 0, iosVersion: null };
  }
  try {
    const s = await Native.getStatus();
    cachedStatus = s;
    return s;
  } catch (e) {
    return { supported: false, enabled: false, count: 0, error: String(e) };
  }
}

/**
 * Chiamata idempotente: se il device supporta e non ha ancora abilitato,
 * chiede al sistema di attivare il Contact Provider (prompt iOS). Se
 * rifiuta, prossimo tentativo alla prossima apertura Rubrica.
 * Silenziosa: mai un toast, mai una modal.
 */
export async function ensureEnabled() {
  if (!Native) return { supported: false };
  if (enableAttempted) return cachedStatus || { supported: true };
  enableAttempted = true;
  try {
    const status = await Native.getStatus();
    cachedStatus = status;
    if (status.supported && !status.enabled) {
      try {
        await Native.enable();
        cachedStatus = await Native.getStatus();
      } catch (e) {
        lastError = `enable(): ${e?.message || String(e)}`;
      }
    }
    return cachedStatus;
  } catch (e) {
    lastError = `getStatus(): ${e?.message || String(e)}`;
    return { supported: false };
  }
}

/**
 * Enable ESPLICITO da user-gesture. Chiamato dal bottone "Attiva contatti"
 * nel banner della Rubrica. A differenza di `ensureEnabled` (silenziosa,
 * fire-and-forget), questa propaga eventuali errori così l'UI può mostrare
 * un toast di fallback ("Vai in Impostazioni iPhone → Contatti → ...").
 */
export async function enableNow() {
  if (!Native) throw new Error('Contact Provider non disponibile su questo dispositivo');
  enableAttempted = true;
  const status = await Native.getStatus();
  cachedStatus = status;
  if (!status.supported) {
    throw new Error(`Richiede iOS 18 o superiore (rilevato ${status.iosVersion || 'sconosciuto'})`);
  }
  if (status.enabled) {
    return status; // già attivo
  }
  await Native.enable();       // può lanciare prompt sistema
  cachedStatus = await Native.getStatus();
  return cachedStatus;
}

/**
 * Mappa un contatto della Rubrica CRM Pronto verso il formato Contact Provider.
 * Contract atteso da backend `/contacts`:
 *   { id, name, phone, email, ... }
 * Il campo `name` è un unico string tipo "Luca Moris" o "Anna Maria Bruni".
 * Lo splittiamo in first + last (ultima parola = cognome, resto = nome).
 */
function contactToDTO(c) {
  const id = String(c.id || c._id || '');
  const nameRaw = (c.name || '').trim();
  const parts = nameRaw.split(/\s+/).filter(Boolean);
  let firstName = '';
  let lastName = '';
  if (parts.length === 1) {
    firstName = parts[0];
    lastName = '';
  } else if (parts.length >= 2) {
    lastName = parts[parts.length - 1];
    firstName = parts.slice(0, -1).join(' ');
  }
  const phone = normalizePhone(c.phone);
  return { id, firstName, lastName, phone, company: undefined };
}

function normalizePhone(raw) {
  if (!raw) return '';
  const clean = String(raw).replace(/\s+/g, '');
  if (clean.startsWith('+')) return clean;
  // Formato italiano senza prefisso: aggiungi +39 se plausibile
  if (/^3\d{8,9}$/.test(clean)) return `+39${clean}`;
  // Formato 00-prefix internazionale
  if (clean.startsWith('00')) return `+${clean.slice(2)}`;
  return clean;
}

/**
 * Sincronizza tutti i contatti Pronto verso il Contact Provider.
 * Silenzioso: no toast, no throw. Se non supportato, no-op.
 *
 * @param {Array} prontoContacts — array dai backend /contacts
 */
export async function syncFromRubrica(prontoContacts) {
  if (!Native) return { supported: false };
  if (!Array.isArray(prontoContacts)) return { supported: true, count: 0 };

  const dtos = prontoContacts
    .map(contactToDTO)
    .filter(d => d.id && d.phone);

  // Debounce: evita di rifare la sync se il payload non è cambiato
  const hash = quickHash(dtos);
  if (hash === lastSyncHash) {
    return { supported: true, skipped: true };
  }

  try {
    const res = await Native.sync({ contacts: dtos });
    lastSyncHash = hash;
    return { supported: true, ...res };
  } catch (e) {
    lastError = `sync(): ${e?.message || String(e)}`;
    // Errore silenzioso: se non ha ancora enabled, la prossima sync riproverà
    return { supported: true, error: String(e) };
  }
}

function quickHash(dtos) {
  return dtos
    .map(d => `${d.id}:${d.firstName}|${d.lastName}|${d.phone}`)
    .sort()
    .join(';');
}

// Reset cache — utile per debug o hard-refresh
export function resetProntoContactsCache() {
  cachedStatus = null;
  enableAttempted = false;
  lastSyncHash = null;
}
